/**
 * The Settings view's information architecture (HS2-91S2YF): three areas, each a
 * set of one-page settings sub-pages shown as an accordion in the settings
 * sidebar. Choosing an area opens its last-used sub-page (remembered per viewer
 * in localStorage by the caller); the first page of each area is its default.
 * Pure data and resolution only, so it is unit-testable without a DOM.
 */
import type { RoleCapabilities, SettingsTab } from './context.js';

export type SettingsArea = SettingsTab;

export type OrganizationPage =
  | 'profile'
  | 'members'
  | 'sites'
  | 'knowledge-base'
  | 'vendors'
  | 'verdict-thresholds'
  | 'landed-cost'
  | 'currencies'
  | 'ai-usage'
  | 'data-privacy';
export type ProjectPage = 'info' | 'knowledge-base';
export type PersonalPage = 'preferences';
export type SettingsPage = OrganizationPage | ProjectPage | PersonalPage;

export interface SettingsPageDef {
  id: SettingsPage;
  label: string;
}

export const SETTINGS_AREAS: Record<SettingsArea, { label: string; pages: readonly SettingsPageDef[] }> = {
  organization: {
    label: 'Organization',
    pages: [
      { id: 'profile', label: 'Profile' },
      { id: 'members', label: 'Members' },
      { id: 'sites', label: 'Sites' },
      { id: 'knowledge-base', label: 'Knowledge Base' },
      { id: 'vendors', label: 'Vendors' },
      { id: 'verdict-thresholds', label: 'Verdict Thresholds' },
      { id: 'landed-cost', label: 'Landed-cost Assumptions' },
      { id: 'currencies', label: 'Currencies' },
      { id: 'ai-usage', label: 'AI Usage & Budget' },
      { id: 'data-privacy', label: 'Data & Privacy' },
    ],
  },
  project: {
    label: 'Project',
    pages: [
      { id: 'info', label: 'Info' },
      { id: 'knowledge-base', label: 'Knowledge Base' },
    ],
  },
  personal: { label: 'Personal', pages: [{ id: 'preferences', label: 'Preferences' }] },
};

/** The settings areas a role may open, in sidebar order. */
export function settingsAreasFor(capabilities: RoleCapabilities): SettingsArea[] {
  return capabilities.organizationSettings ? ['organization', 'project', 'personal'] : ['personal'];
}

/** The sub-page to open in an area: the remembered one when it belongs to the area,
 *  else the area's first (default) page. */
export function settingsPageFor(area: SettingsArea, remembered: string | undefined): SettingsPage {
  const pages = SETTINGS_AREAS[area].pages;
  return pages.find((page) => page.id === remembered)?.id ?? pages[0]?.id ?? 'preferences';
}

/** The label of a page within its area. */
export function settingsPageLabel(area: SettingsArea, page: SettingsPage): string {
  return SETTINGS_AREAS[area].pages.find((candidate) => candidate.id === page)?.label ?? '';
}

/**
 * Where the current route sits in the settings sidebar, or null outside Settings.
 * Knowledge Base and Vendors keep their own views (the library chat and loaders
 * depend on them); the library's scope tells the organization and project
 * Knowledge Base pages apart.
 */
export function settingsLocation(
  view: string,
  state: { area: SettingsArea; page: SettingsPage; libraryScope: string },
): { area: SettingsArea; page: SettingsPage } | null {
  if (view === 'vendors') return { area: 'organization', page: 'vendors' };
  if (view === 'library') {
    return { area: state.libraryScope === '' ? 'organization' : 'project', page: 'knowledge-base' };
  }
  if (view === 'settings') return { area: state.area, page: settingsPageFor(state.area, state.page) };
  return null;
}
